import { useId, type ReactNode } from 'react';

/**
 * Ported from src/components/ui/TextField.tsx. `onChangeText` matches the React Native
 * signature so screens ported from the app do not have to unwrap events.
 */
export default function TextField({
  label,
  value,
  onChangeText,
  placeholder,
  multiline = false,
  type = 'text',
  error,
  hint,
  autoFocus,
  rows = 4,
  autoComplete,
  inputMode,
  id: givenId,
  onBlur,
  maxLength,
  hintHidden = false,
  trailing,
  children,
}: {
  label?: string;
  value: string;
  onChangeText: (text: string) => void;
  placeholder?: string;
  multiline?: boolean;
  type?: 'text' | 'email' | 'password' | 'search';
  error?: string;
  /** Said before anything goes wrong — a rule the value must meet — and read with the field. */
  hint?: string;
  autoFocus?: boolean;
  rows?: number;
  /**
   * What the field is for, in the browser's words: 'email', 'new-password', 'current-password',
   * 'one-time-code'. Without it iOS offers no code from Mail and no password manager fills or
   * saves anything, so the sign-up funnel was typed by hand end to end (F7, 2026-09-30).
   */
  autoComplete?: string;
  /** The keyboard a phone shows: 'numeric' for a code. */
  inputMode?: 'text' | 'email' | 'numeric' | 'search';
  /** For a control outside the field that names it (Sign up's show-password toggle, aria-controls). */
  id?: string;
  onBlur?: () => void;
  maxLength?: number;
  /**
   * The hint is read with the field but not shown, because something on screen says it better
   * (Sign up's ticking checklist says the password rule part by part).
   */
  hintHidden?: boolean;
  /** Inside the field's right edge: a button that acts on it, like Show password. */
  trailing?: ReactNode;
  /** Under the field and its hint, above any error: what the field needs, shown as it is met. */
  children?: ReactNode;
}) {
  const ownId = useId();
  const id = givenId ?? ownId;
  const errorId = `${id}-error`;
  const hintId = `${id}-hint`;
  const describedBy = [hint ? hintId : null, error ? errorId : null].filter(Boolean).join(' ');
  const shared = {
    id,
    value,
    placeholder,
    autoFocus,
    autoComplete,
    inputMode,
    onBlur,
    maxLength,
    'aria-invalid': error ? (true as const) : undefined,
    'aria-describedby': describedBy || undefined,
    className: `field ${error ? 'field-error' : ''} ${trailing ? 'field-has-trailing' : ''}`.replace(/\s+/g, ' ').trim(),
    onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => onChangeText(e.target.value),
  };
  return (
    <div className="field-wrap">
      {label && <label htmlFor={id} className="type-caption field-label">{label}</label>}
      {multiline ? (
        <textarea {...shared} rows={rows} />
      ) : trailing ? (
        <div className="field-affix">
          <input {...shared} type={type} />
          {trailing}
        </div>
      ) : (
        <input {...shared} type={type} />
      )}
      {hint && <p id={hintId} className={hintHidden ? 'visually-hidden' : 'type-caption field-hint'}>{hint}</p>}
      {children}
      {error && <p id={errorId} role="alert" className="type-caption field-error-text">{error}</p>}
    </div>
  );
}
